import { parse } from 'yaml';

import type { WorkflowStepType } from '@/lib/api/generated/model';

export interface OutlineStep {
  id: string;
  name: string;
  /**
   * The product's own step type, or null when the definition names one this
   * build does not know. Kept rather than reduced to a flag so the step can
   * carry the same mark the workflow canvas gives it.
   */
  kind: WorkflowStepType | null;
  /** True for a step that waits for a person rather than running. */
  waitsForAPerson: boolean;
  /**
   * The key of the workflow a linked step hands its work to, as its definition
   * names it (spec 109 US1); null for every other step, and for a linked step
   * that names none.
   */
  links: string | null;
}

export interface OutlinePhase {
  id: string;
  name: string;
  steps: OutlineStep[];
}

export interface WorkflowOutline {
  phases: OutlinePhase[];
}

/** The step types a definition can name, in the product's own vocabulary. */
const HUMAN_GATE = 'human_gate';
const AI_STEP = 'ai_step';
const LINKED_WORKFLOW = 'linked_workflow';

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

/**
 * A library workflow's phases and steps, from its definition (spec 105 FR-024).
 *
 * Parsed here rather than served, so the read door stays a plain file reader
 * that serves agents, skills and workflows alike (research R9). What the reader
 * wants is the shape — how many steps, what they are called, where it stops for
 * a person — not the operating instructions the definition carries.
 *
 * Total: anything that will not parse, or parses to something that is not a
 * workflow, returns `null`, and the drawer says so in one line. This is a
 * preview, and a definition the page cannot read is not a reason to stop
 * somebody installing a workflow the service can.
 */
export function workflowOutline(definition: string): WorkflowOutline | null {
  let document: unknown;
  try {
    document = parse(definition);
  } catch {
    return null;
  }
  if (typeof document !== 'object' || document === null) return null;
  const phases = (document as { phases?: unknown }).phases;
  if (!Array.isArray(phases)) return null;

  const out: OutlinePhase[] = [];
  phases.forEach((phase, phaseIndex) => {
    if (typeof phase !== 'object' || phase === null) return;
    const rawSteps = (phase as { steps?: unknown }).steps;
    const steps: OutlineStep[] = [];
    if (Array.isArray(rawSteps)) {
      rawSteps.forEach((step, stepIndex) => {
        if (typeof step !== 'object' || step === null) return;
        const s = step as {
          id?: unknown;
          name?: unknown;
          type?: unknown;
          workflow?: unknown;
        };
        const id = text(s.id, `step-${String(stepIndex)}`);
        const kind =
          s.type === HUMAN_GATE ||
          s.type === AI_STEP ||
          s.type === LINKED_WORKFLOW
            ? (s.type as WorkflowStepType)
            : null;
        const linked = kind === LINKED_WORKFLOW ? text(s.workflow, '') : '';
        steps.push({
          id,
          name: text(s.name, id),
          kind,
          waitsForAPerson: kind === HUMAN_GATE,
          links: linked || null,
        });
      });
    }
    const p = phase as { id?: unknown; name?: unknown };
    const id = text(p.id, `phase-${String(phaseIndex)}`);
    out.push({ id, name: text(p.name, id), steps });
  });
  return out.length > 0 ? { phases: out } : null;
}
